/**
 * useApi — lightweight replacement for @tanstack/react-query's useQuery / useSuspenseQuery.
 *
 * Usage:
 *   const { data, loading, error, refetch } = useApi(queryKey, queryFn);
 *   const { data } = useApi(cartQuery().queryKey, cartQuery().queryFn);
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { queryCache } from "./queryCache";

export interface UseApiResult<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching: boolean;
}

export interface UseApiResultDefined<T> extends UseApiResult<T> {
  data: T;
}

/**
 * Overload: returns `data` as possibly-undefined when no initialData is given.
 */
export function useApi<T>(
  queryKey: unknown[],
  queryFn: () => Promise<T>,
  options?: { enabled?: boolean; placeholderData?: T }
): UseApiResult<T> {
  const [, forceUpdate] = useState(0);
  const enabled = options?.enabled !== false;
  const keyStr = JSON.stringify(queryKey);
  const cached = queryCache.get<T>(queryKey);

  const loadingRef = useRef(cached === undefined && enabled);
  const fetchingRef = useRef(enabled);
  const errorRef = useRef<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const dataRef = useRef<T | undefined>(cached ?? options?.placeholderData);
  const keyRef = useRef(keyStr);

  // The query key changed during this render (e.g. a new category tab): swap to
  // the cached value for the *new* key immediately so the first render after the
  // click already shows the right data instead of the previous key's rows.
  if (keyRef.current !== keyStr) {
    keyRef.current = keyStr;
    dataRef.current = cached ?? options?.placeholderData;
    errorRef.current = null;
    loadingRef.current = (cached === undefined) && enabled;
    fetchingRef.current = enabled;
  }


  const fetch = useCallback(async () => {
    if (!enabled) return;
    fetchingRef.current = true;
    if (dataRef.current === undefined) loadingRef.current = true;
    errorRef.current = null;
    forceUpdate((n) => n + 1);

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const result = await queryFn();
      queryCache.set(queryKey, result);
      dataRef.current = result;
      errorRef.current = null;
    } catch (err) {
      errorRef.current = err instanceof Error ? err : new Error(String(err));
    } finally {
      loadingRef.current = false;
      fetchingRef.current = false;
      forceUpdate((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queryKey), enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Stale-while-revalidate: show any cached value instantly, but always
    // re-fetch when the component mounts (or the key changes) so data edited
    // elsewhere — e.g. a product added in the admin panel — shows up without
    // a full page refresh.
    const fresh = queryCache.get<T>(queryKey);
    if (fresh !== undefined) {
      dataRef.current = fresh;
    }
    void fetch();

    const unsubscribe = queryCache.subscribe(queryKey, () => {
      const updated = queryCache.get<T>(queryKey);
      if (updated !== undefined) {
        dataRef.current = updated;
        forceUpdate((n) => n + 1);
      } else {
        void fetch();
      }
    });

    // Re-fetch when the user comes back to the tab/window.
    const revalidate = () => {
      if (document.visibilityState === "visible") void fetch();
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queryKey), enabled]);


  return {
    data: dataRef.current,
    loading: loadingRef.current,
    isFetching: fetchingRef.current,
    error: errorRef.current,
    refetch: fetch,
  };
}
